import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, CheckCircle, ChevronLeft, ChevronRight, ExternalLink, FileText, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API, getHeaders } from '../portalShared';

export default function BrandInsights() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [activeView, setActiveView] = useState(null); // 'testimonials' | 'surveys'
  const [items, setItems] = useState([]);
  const [surveyData, setSurveyData] = useState(null); // {survey, responses, summary}
  const [feedback, setFeedback] = useState({});
  const [processing, setProcessing] = useState('');

  useEffect(() => {
    axios.get(`${API}/portal/campaigns`, { headers: getHeaders() })
      .then(r => setCampaigns(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const offeringCampaigns = campaigns.filter(c =>
    ['written_testimonial', 'survey', 'video_testimonial', 'feedback_session', 'advisory_call', 'consulting_project', 'sales_pitch', 'ai_training'].includes(c.offering_type)
  );

  const openCampaign = async (c) => {
    setActiveCampaign(c);
    setItems([]);
    setSurveyData(null);
    if (c.offering_type === 'written_testimonial') {
      setActiveView('testimonials');
      try { const { data } = await axios.get(`${API}/portal/testimonials/${c.id}`, { headers: getHeaders() }); setItems(data || []); }
      catch { toast.error('Failed to load testimonials'); }
    } else if (c.offering_type === 'video_testimonial') {
      setActiveView('video_testimonials');
      try { const { data } = await axios.get(`${API}/portal/video-testimonials/${c.id}`, { headers: getHeaders() }); setItems(data || []); }
      catch { toast.error('Failed to load video testimonials'); }
    } else if (c.offering_type === 'survey') {
      setActiveView('surveys');
      try { const { data } = await axios.get(`${API}/portal/surveys/${c.id}/responses`, { headers: getHeaders() }); setSurveyData(data); }
      catch { toast.error('Failed to load survey responses'); }
    } else if (c.offering_type === 'feedback_session' || c.offering_type === 'advisory_call' || c.offering_type === 'sales_pitch') {
      setActiveView('sessions');
      try { const { data } = await axios.get(`${API}/portal/sessions/${c.id}`, { headers: getHeaders() }); setItems(data || []); }
      catch { toast.error('Failed to load sessions'); }
    } else if (c.offering_type === 'consulting_project') {
      setActiveView('project');
      try { const { data } = await axios.get(`${API}/portal/projects/by-campaign/${c.id}`, { headers: getHeaders() }); setSurveyData(data); /* reuse surveyData state */ }
      catch { toast.error('No project defined yet'); }
    } else if (c.offering_type === 'ai_training') {
      setActiveView('ai_training');
      try { const { data } = await axios.get(`${API}/portal/ai-training/${c.id}/responses`, { headers: getHeaders() }); setSurveyData(data); }
      catch { toast.error('Failed to load AI training data'); }
    }
  };

  const approveTestimonial = async (id) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/testimonials/${id}/approve`, {}, { headers: getHeaders() });
      toast.success('Testimonial approved');
      const { data } = await axios.get(`${API}/portal/testimonials/${activeCampaign.id}`, { headers: getHeaders() });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const requestTestimonialRevision = async (id) => {
    const fb = (feedback[id] || '').trim();
    if (!fb) { toast.error('Please provide revision notes'); return; }
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/testimonials/${id}/request-revision`, { feedback: fb }, { headers: getHeaders() });
      toast.success('Revision requested');
      setFeedback({ ...feedback, [id]: '' });
      const { data } = await axios.get(`${API}/portal/testimonials/${activeCampaign.id}`, { headers: getHeaders() });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  // Phase 2 review handlers
  const approveVideo = async (id) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/video-testimonials/${id}/approve`, {}, { headers: getHeaders() });
      toast.success('Video approved');
      const { data } = await axios.get(`${API}/portal/video-testimonials/${activeCampaign.id}`, { headers: getHeaders() });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const requestVideoRevision = async (id) => {
    const fb = (feedback[id] || '').trim();
    if (!fb) { toast.error('Please provide revision notes'); return; }
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/video-testimonials/${id}/request-revision`, { feedback: fb }, { headers: getHeaders() });
      toast.success('Revision requested');
      setFeedback({ ...feedback, [id]: '' });
      const { data } = await axios.get(`${API}/portal/video-testimonials/${activeCampaign.id}`, { headers: getHeaders() });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const confirmSessionSlot = async (sessionId, chosenSlot) => {
    const meetingUrl = (feedback[sessionId + '_url'] || '').trim();
    if (!meetingUrl) { toast.error('Add a meeting URL'); return; }
    setProcessing(sessionId);
    try {
      await axios.post(`${API}/portal/sessions/${sessionId}/confirm`, { chosen_slot: chosenSlot, meeting_url: meetingUrl }, { headers: getHeaders() });
      toast.success('Session confirmed');
      const { data } = await axios.get(`${API}/portal/sessions/${activeCampaign.id}`, { headers: getHeaders() });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const rateSession = async (sessionId, rating) => {
    setProcessing(sessionId);
    try {
      await axios.post(`${API}/portal/sessions/${sessionId}/rate`, { rating }, { headers: getHeaders() });
      toast.success(`Rated ${rating}/5`);
      const { data } = await axios.get(`${API}/portal/sessions/${activeCampaign.id}`, { headers: getHeaders() });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const approveMilestone = async (projectId, milestoneId) => {
    setProcessing(milestoneId);
    try {
      const { data } = await axios.post(`${API}/portal/projects/${projectId}/milestones/${milestoneId}/approve`, {}, { headers: getHeaders() });
      toast.success(`Milestone approved (payout: ${data.payout_status})`);
      const { data: p } = await axios.get(`${API}/portal/projects/by-campaign/${activeCampaign.id}`, { headers: getHeaders() });
      setSurveyData(p);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const requestMilestoneRevision = async (projectId, milestoneId) => {
    const fb = (feedback[milestoneId] || '').trim();
    if (!fb) { toast.error('Please provide revision notes'); return; }
    setProcessing(milestoneId);
    try {
      await axios.post(`${API}/portal/projects/${projectId}/milestones/${milestoneId}/request-revision`, { feedback: fb }, { headers: getHeaders() });
      toast.success('Revision requested');
      setFeedback({ ...feedback, [milestoneId]: '' });
      const { data: p } = await axios.get(`${API}/portal/projects/by-campaign/${activeCampaign.id}`, { headers: getHeaders() });
      setSurveyData(p);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading...</p>;

  // Campaign picker
  if (!activeCampaign) {
    return (
      <div className="space-y-6" data-testid="brand-insights-section">
        <h2 className="font-display text-xl font-bold">Deliverables <span className="text-[#FF4343]">Hub</span></h2>
        <p className="text-xs text-[#6B7280]">Review written + video testimonials, survey responses, scheduled sessions and consulting milestones from your executive endorsers.</p>
        {offeringCampaigns.length === 0 ? (
          <div className="text-center py-12 bg-[#111827] border border-white/10">
            <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
            <p className="text-[#9CA3AF] text-sm mb-1">No testimonial or survey campaigns yet.</p>
            <p className="text-[10px] text-[#6B7280]">Create a campaign and pick "Written Testimonial" or "Survey" to start.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {offeringCampaigns.map(c => (
              <button key={c.id} onClick={() => openCampaign(c)} data-testid={`insights-campaign-${c.id}`}
                className="text-left bg-[#111827] border border-white/10 hover:border-[#FF4343]/40 p-4 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[9px] font-mono uppercase tracking-wider text-[#FF4343]">
                    {({
                      written_testimonial: 'Written Testimonial',
                      video_testimonial: 'Video Testimonial',
                      survey: 'Survey',
                      feedback_session: 'Feedback Session',
                      advisory_call: 'Advisory Call',
                      consulting_project: 'Consulting Project',
                      sales_pitch: 'Sales Pitch',
                      ai_training: 'AI Training Q&A',
                    })[c.offering_type] || c.offering_type}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1">{c.title}</h3>
                <p className="text-[10px] text-[#6B7280]">{c.industry || '—'} · {c.spots_filled || 0}/{c.spots_total || 0} spots</p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Testimonials view
  if (activeView === 'testimonials') {
    const pending = items.filter(t => t.status === 'submitted');
    const approved = items.filter(t => t.status === 'approved');
    const revision = items.filter(t => t.status === 'revision_requested');
    return (
      <div className="space-y-5" data-testid="testimonials-review-section">
        <button onClick={() => { setActiveCampaign(null); setItems([]); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to campaigns
        </button>
        <h2 className="font-display text-xl font-bold">{activeCampaign.title} <span className="text-[#FF4343]">Testimonials</span></h2>

        {pending.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-[#FF4343] uppercase tracking-wider">Awaiting Review ({pending.length})</h3>
            {pending.map(t => (
              <div key={t.id} className="bg-[#111827] border border-[#FF4343]/30 p-4" data-testid={`testimonial-${t.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-semibold text-white">{t.executive_name}</span>
                    <span className="text-[10px] text-[#6B7280] ml-2">{t.title_at_time}{t.company_at_time ? ` · ${t.company_at_time}` : ''}</span>
                  </div>
                  <span className="text-[10px] text-yellow-400 font-mono uppercase">{t.word_count} words</span>
                </div>
                <blockquote className="bg-white/5 border-l-2 border-[#FF4343] p-3 mb-3 text-sm text-[#9CA3AF] italic whitespace-pre-wrap">"{t.text}"</blockquote>
                <textarea value={feedback[t.id] || ''} onChange={e => setFeedback({ ...feedback, [t.id]: e.target.value })}
                  placeholder="Revision notes (required if requesting changes)..." rows={2}
                  data-testid={`testimonial-feedback-${t.id}`}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-3" />
                <div className="flex gap-2">
                  <button onClick={() => approveTestimonial(t.id)} disabled={processing === t.id} data-testid={`approve-testimonial-${t.id}`}
                    className="flex-1 flex items-center justify-center gap-1 border-2 border-[#10B981] bg-[#10B981] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#059669] transition-all disabled:opacity-50">
                    <CheckCircle className="w-3 h-3" /> Approve
                  </button>
                  <button onClick={() => requestTestimonialRevision(t.id)} disabled={processing === t.id} data-testid={`revision-testimonial-${t.id}`}
                    className="flex-1 flex items-center justify-center gap-1 border border-red-500/50 text-red-400 py-2 text-xs font-medium hover:bg-red-500/10 transition-all disabled:opacity-50">
                    <XCircle className="w-3 h-3" /> Request Revision
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {revision.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">Revision Requested ({revision.length})</h3>
            {revision.map(t => (
              <div key={t.id} className="bg-[#111827] border border-yellow-500/20 p-4">
                <span className="text-sm font-semibold text-white">{t.executive_name}</span>
                <blockquote className="bg-white/5 p-3 mt-2 text-xs text-[#9CA3AF] italic whitespace-pre-wrap">"{t.text}"</blockquote>
                <p className="text-[10px] text-yellow-400 mt-2">Your notes: {t.brand_feedback}</p>
              </div>
            ))}
          </div>
        )}

        {approved.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-[#10B981] uppercase tracking-wider">Approved ({approved.length})</h3>
            {approved.map(t => (
              <div key={t.id} className="bg-[#111827] border border-[#10B981]/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{t.executive_name} <span className="text-xs text-[#6B7280]">· {t.title_at_time}</span></span>
                  <CheckCircle className="w-4 h-4 text-[#10B981]" />
                </div>
                <blockquote className="bg-white/5 border-l-2 border-[#10B981] p-3 text-sm text-[#9CA3AF] italic whitespace-pre-wrap">"{t.text}"</blockquote>
              </div>
            ))}
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center py-12 bg-[#111827] border border-white/10">
            <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
            <p className="text-[#9CA3AF] text-sm">No testimonials submitted yet.</p>
          </div>
        )}
      </div>
    );
  }

  // Surveys view
  if (activeView === 'surveys') {
    const survey = surveyData?.survey;
    const responses = surveyData?.responses || [];
    const summary = surveyData?.summary || {};
    return (
      <div className="space-y-5" data-testid="surveys-review-section">
        <button onClick={() => { setActiveCampaign(null); setSurveyData(null); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to campaigns
        </button>
        <h2 className="font-display text-xl font-bold">{activeCampaign.title} <span className="text-[#FF4343]">Survey Responses</span></h2>

        {!survey ? (
          <div className="text-center py-12 bg-[#111827] border border-white/10">
            <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
            <p className="text-[#9CA3AF] text-sm">Survey not configured yet.</p>
          </div>
        ) : (
          <>
            <div className="bg-[#111827] border border-white/10 p-4 grid grid-cols-3 gap-4">
              <div><div className="text-xl font-mono text-white">{responses.length}</div><div className="text-[10px] text-[#6B7280] font-mono uppercase">Responses</div></div>
              <div><div className="text-xl font-mono text-[#FF4343]">{(survey.questions || []).length}</div><div className="text-[10px] text-[#6B7280] font-mono uppercase">Questions</div></div>
              <div><div className="text-xl font-mono text-white">{survey.external_url ? 'External' : 'Native'}</div><div className="text-[10px] text-[#6B7280] font-mono uppercase">Type</div></div>
            </div>

            {/* Question summaries (native only) */}
            {!survey.external_url && (survey.questions || []).length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-[#FF4343] uppercase tracking-wider">Question Summaries</h3>
                {(survey.questions || []).map((q, qi) => {
                  const s = summary[q.id] || {};
                  return (
                    <div key={q.id} className="bg-[#111827] border border-white/10 p-4" data-testid={`survey-summary-q-${qi}`}>
                      <p className="text-xs text-white mb-2"><span className="text-[#6B7280]">Q{qi + 1}.</span> {q.prompt}</p>
                      {s.type === 'rating' && (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div><span className="text-[#6B7280]">Avg:</span> <span className="text-[#FF4343] font-mono">{s.avg}</span></div>
                          <div><span className="text-[#6B7280]">Min:</span> <span className="text-white font-mono">{s.min}</span></div>
                          <div><span className="text-[#6B7280]">Max:</span> <span className="text-white font-mono">{s.max}</span></div>
                        </div>
                      )}
                      {s.type === 'multiple_choice' && (
                        <div className="space-y-1.5">
                          {Object.entries(s.counts || {}).map(([opt, cnt]) => (
                            <div key={opt} className="flex items-center gap-3">
                              <div className="text-xs text-[#9CA3AF] flex-1">{opt}</div>
                              <div className="flex-1 h-1.5 bg-white/5 relative">
                                <div className="absolute inset-y-0 left-0 bg-[#FF4343]" style={{ width: `${responses.length ? (cnt / responses.length) * 100 : 0}%` }} />
                              </div>
                              <div className="text-xs text-white font-mono w-8 text-right">{cnt}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {(s.type === 'short_text' || s.type === 'long_text') && (
                        <div className="text-[10px] text-[#6B7280]">{s.count || 0} text responses (see individual responses below)</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Individual responses */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-[#FF4343] uppercase tracking-wider">Individual Responses ({responses.length})</h3>
              {responses.length === 0 ? (
                <div className="text-center py-6 bg-[#111827] border border-white/10 text-xs text-[#6B7280]">No responses yet.</div>
              ) : responses.map(r => (
                <div key={r.id} className="bg-[#111827] border border-white/10 p-4" data-testid={`survey-response-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white">{r.executive_name}</span>
                    <span className="text-[10px] text-[#6B7280] font-mono">{r.submitted_at?.slice(0, 10)}</span>
                  </div>
                  {r.external_completed ? (
                    <p className="text-[10px] text-[#9CA3AF]">Completed external survey.</p>
                  ) : (
                    <div className="space-y-2">
                      {(survey.questions || []).map((q, qi) => (
                        <div key={q.id} className="text-xs">
                          <span className="text-[#6B7280]">Q{qi + 1}:</span> <span className="text-[#9CA3AF]">{q.prompt}</span>
                          <div className="text-white pl-3 mt-0.5">{r.answers?.[q.id] || <span className="text-[#6B7280] italic">(no answer)</span>}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Video Testimonials view
  if (activeView === 'video_testimonials') {
    const pending = items.filter(t => t.status === 'submitted');
    const approved = items.filter(t => t.status === 'approved');
    return (
      <div className="space-y-5" data-testid="video-testimonials-review-section">
        <button onClick={() => { setActiveCampaign(null); setItems([]); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to campaigns
        </button>
        <h2 className="font-display text-xl font-bold">{activeCampaign.title} <span className="text-[#FF4343]">Video Submissions</span></h2>
        {pending.length === 0 && approved.length === 0 ? (
          <div className="text-center py-12 bg-[#111827] border border-white/10"><FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" /><p className="text-[#9CA3AF] text-sm">No video submissions yet.</p></div>
        ) : (
          <>
            {pending.length > 0 && <h3 className="text-xs font-semibold text-[#FF4343] uppercase tracking-wider">Awaiting Review ({pending.length})</h3>}
            {pending.map(v => (
              <div key={v.id} className="bg-[#111827] border border-[#FF4343]/30 p-4" data-testid={`video-testimonial-${v.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{v.executive_name} <span className="text-xs text-[#6B7280]">· {v.title_at_time}</span></span>
                  {v.duration_sec && <span className="text-[10px] text-[#6B7280] font-mono">{v.duration_sec}s</span>}
                </div>
                <video src={v.video_url} controls className="w-full max-h-72 mb-3 bg-black" />
                <textarea value={feedback[v.id] || ''} onChange={e => setFeedback({ ...feedback, [v.id]: e.target.value })}
                  placeholder="Revision notes (required if requesting changes)..." rows={2}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-3" />
                <div className="flex gap-2">
                  <button onClick={() => approveVideo(v.id)} disabled={processing === v.id} data-testid={`approve-video-${v.id}`}
                    className="flex-1 flex items-center justify-center gap-1 border-2 border-[#10B981] bg-[#10B981] text-[#090E33] py-2 text-xs font-semibold disabled:opacity-50"><CheckCircle className="w-3 h-3" /> Approve</button>
                  <button onClick={() => requestVideoRevision(v.id)} disabled={processing === v.id}
                    className="flex-1 flex items-center justify-center gap-1 border border-red-500/50 text-red-400 py-2 text-xs font-medium disabled:opacity-50"><XCircle className="w-3 h-3" /> Request Revision</button>
                </div>
              </div>
            ))}
            {approved.length > 0 && <h3 className="text-xs font-semibold text-[#10B981] uppercase tracking-wider">Approved ({approved.length})</h3>}
            {approved.map(v => (
              <div key={v.id} className="bg-[#111827] border border-[#10B981]/20 p-4">
                <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-white">{v.executive_name}</span><CheckCircle className="w-4 h-4 text-[#10B981]" /></div>
                <video src={v.video_url} controls className="w-full max-h-60 bg-black" />
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // Sessions view (feedback_session + advisory_call)
  if (activeView === 'sessions') {
    return (
      <div className="space-y-5" data-testid="sessions-review-section">
        <button onClick={() => { setActiveCampaign(null); setItems([]); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to campaigns
        </button>
        <h2 className="font-display text-xl font-bold">{activeCampaign.title} <span className="text-[#FF4343]">Sessions</span></h2>
        {items.length === 0 ? (
          <div className="text-center py-12 bg-[#111827] border border-white/10"><CalendarIcon className="w-10 h-10 text-[#6B7280] mx-auto mb-3" /><p className="text-[#9CA3AF] text-sm">No session proposals yet.</p></div>
        ) : items.map(s => (
          <div key={s.id} className="bg-[#111827] border border-white/10 p-4" data-testid={`session-${s.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-white">{s.executive_name}</span>
              <span className="text-[10px] font-mono uppercase text-[#FF4343]">{s.status}</span>
            </div>
            {s.status === 'proposed' && (
              <div className="space-y-2 mt-3">
                <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Proposed Slots — Pick One</p>
                {s.proposed_slots.map((slot, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/5 p-2">
                    <span className="text-xs text-white flex-1">{new Date(slot).toLocaleString()}</span>
                    <button onClick={() => confirmSessionSlot(s.id, slot)} disabled={processing === s.id} data-testid={`confirm-slot-${s.id}-${i}`}
                      className="px-3 py-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] text-[10px] font-semibold disabled:opacity-50">Confirm</button>
                  </div>
                ))}
                <input value={feedback[s.id + '_url'] || ''} onChange={e => setFeedback({ ...feedback, [s.id + '_url']: e.target.value })}
                  data-testid={`session-meeting-url-${s.id}`}
                  placeholder="Zoom / Google Meet URL (required to confirm)" className="w-full mt-2 px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343]" />
              </div>
            )}
            {s.status === 'confirmed' && (
              <div className="text-xs space-y-1 mt-2">
                <p className="text-[#9CA3AF]">Confirmed: <strong className="text-white">{new Date(s.confirmed_slot).toLocaleString()}</strong></p>
                {s.meeting_url && <a href={s.meeting_url} target="_blank" rel="noopener noreferrer" className="text-[#FF4343] hover:underline inline-flex items-center gap-1">Join meeting <ExternalLink className="w-3 h-3" /></a>}
              </div>
            )}
            {s.status === 'completed' && (
              <div className="text-xs space-y-2 mt-2">
                <p className="text-[#10B981]">Completed: <strong className="text-white">{new Date(s.confirmed_slot).toLocaleString()}</strong></p>
                {s.post_session_notes && <div className="bg-white/5 p-3 border-l-2 border-[#10B981]"><p className="text-[10px] text-[#6B7280] uppercase font-mono mb-1">Exec Notes</p><p className="text-[#9CA3AF]">{s.post_session_notes}</p></div>}
                {!s.brand_rating ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#6B7280]">Rate this session:</span>
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} onClick={() => rateSession(s.id, n)} disabled={processing === s.id} data-testid={`rate-session-${s.id}-${n}`}
                        className="w-8 h-8 border border-white/10 text-[#9CA3AF] hover:border-[#FF4343] hover:text-[#FF4343]">{n}</button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#FF4343] font-mono">Rated: {s.brand_rating}/5 ★</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Consulting Project view — surveyData holds the project object
  if (activeView === 'project') {
    const project = surveyData;
    return (
      <div className="space-y-5" data-testid="project-review-section">
        <button onClick={() => { setActiveCampaign(null); setSurveyData(null); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to campaigns
        </button>
        <h2 className="font-display text-xl font-bold">{activeCampaign.title} <span className="text-[#FF4343]">Project</span></h2>
        {!project ? (
          <div className="text-center py-12 bg-[#111827] border border-white/10"><FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" /><p className="text-[#9CA3AF] text-sm">No project scope defined yet.</p></div>
        ) : (
          <>
            <div className="bg-[#111827] border border-white/10 p-4">
              <p className="text-[10px] text-[#6B7280] uppercase font-mono mb-2">Scope of Work</p>
              <p className="text-sm text-[#9CA3AF] whitespace-pre-wrap">{project.scope}</p>
            </div>
            <h3 className="text-xs font-semibold text-[#FF4343] uppercase tracking-wider">Milestones ({project.milestones.length})</h3>
            {project.milestones.map((m, mi) => (
              <div key={m.id} className="bg-[#111827] border border-white/10 p-4" data-testid={`milestone-review-${m.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-semibold text-white">M{mi + 1}: {m.title}</span>
                    <span className="text-[10px] text-[#6B7280] ml-2 font-mono">${m.amount?.toLocaleString()}</span>
                  </div>
                  <span className="text-[10px] font-mono uppercase text-[#FF4343]">{m.status}</span>
                </div>
                {m.description && <p className="text-xs text-[#9CA3AF] mb-2">{m.description}</p>}
                {m.status === 'submitted' && (
                  <div className="space-y-2 bg-white/5 p-3 mt-2">
                    {m.deliverable_url && <a href={m.deliverable_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF4343] hover:underline inline-flex items-center gap-1">View deliverable <ExternalLink className="w-3 h-3" /></a>}
                    {m.deliverable_notes && <p className="text-xs text-[#9CA3AF] whitespace-pre-wrap">{m.deliverable_notes}</p>}
                    <textarea value={feedback[m.id] || ''} onChange={e => setFeedback({ ...feedback, [m.id]: e.target.value })}
                      placeholder="Revision notes (if rejecting)..." rows={2}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                    <div className="flex gap-2">
                      <button onClick={() => approveMilestone(project.id, m.id)} disabled={processing === m.id} data-testid={`approve-milestone-${m.id}`}
                        className="flex-1 border-2 border-[#10B981] bg-[#10B981] text-[#090E33] py-2 text-xs font-semibold disabled:opacity-50">Approve &amp; Pay ${m.amount?.toLocaleString()}</button>
                      <button onClick={() => requestMilestoneRevision(project.id, m.id)} disabled={processing === m.id}
                        className="flex-1 border border-red-500/50 text-red-400 py-2 text-xs disabled:opacity-50">Request Revision</button>
                    </div>
                  </div>
                )}
                {m.status === 'approved' && (
                  <p className="text-[10px] text-[#10B981] mt-2 font-mono">✓ Approved · Payout: {m.payout_status}{m.payout_transfer_id ? ` (${m.payout_transfer_id.slice(0, 14)}…)` : ''}</p>
                )}
                {m.status === 'revision_requested' && m.brand_feedback && (
                  <p className="text-[10px] text-yellow-400 mt-2">Your notes: {m.brand_feedback}</p>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // AI Training Q&A view
  if (activeView === 'ai_training') {
    const qset = surveyData?.question_set;
    const responses = surveyData?.responses || [];
    const exportUrl = `${API}/portal/ai-training/${activeCampaign.id}/export.jsonl`;
    return (
      <div className="space-y-5" data-testid="ai-training-review-section">
        <button onClick={() => { setActiveCampaign(null); setSurveyData(null); }} className="text-xs text-[#9CA3AF] hover:text-white flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to campaigns
        </button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-xl font-bold">{activeCampaign.title} <span className="text-[#FF4343]">AI Training</span></h2>
          {responses.length > 0 && (
            <a href={exportUrl} download
              onClick={async (e) => {
                e.preventDefault();
                try {
                  const res = await axios.get(exportUrl, { headers: getHeaders(), responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a'); a.href = url; a.download = `${activeCampaign.title.replace(/ /g, '_')}.jsonl`; a.click();
                  toast.success('JSONL downloaded — ready to fine-tune!');
                } catch { toast.error('Export failed'); }
              }}
              data-testid="ai-training-export-jsonl"
              className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-4 py-2 text-xs font-semibold inline-flex items-center gap-2 hover:bg-[#E03030]">
              ⬇ Download Training JSONL
            </a>
          )}
        </div>
        {!qset ? (
          <div className="text-center py-12 bg-[#111827] border border-white/10"><FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" /><p className="text-[#9CA3AF] text-sm">Question set not configured yet.</p></div>
        ) : (
          <>
            <div className="bg-[#111827] border border-white/10 p-4 grid grid-cols-3 gap-4">
              <div><div className="text-xl font-mono text-white">{responses.length}</div><div className="text-[10px] text-[#6B7280] font-mono uppercase">Submissions</div></div>
              <div><div className="text-xl font-mono text-[#FF4343]">{(qset.questions || []).length}</div><div className="text-[10px] text-[#6B7280] font-mono uppercase">Questions</div></div>
              <div><div className="text-xl font-mono text-white">{responses.reduce((s, r) => s + Object.values(r.answers || {}).filter(Boolean).length, 0)}</div><div className="text-[10px] text-[#6B7280] font-mono uppercase">Q&amp;A pairs</div></div>
            </div>
            <div className="space-y-3">
              {responses.length === 0 ? (
                <div className="text-center py-6 bg-[#111827] border border-white/10 text-xs text-[#6B7280]">No submissions yet. Executives accept the campaign and answer the questions.</div>
              ) : responses.map((r, ri) => (
                <div key={r.participant_id || ri} className="bg-[#111827] border border-white/10 p-4" data-testid={`ai-training-response-${ri}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-semibold text-white">{r.executive_name}</span>
                      <span className="text-[10px] text-[#6B7280] ml-2">{r.executive_title}</span>
                    </div>
                    <span className="text-[10px] font-mono uppercase text-[#FF4343]">{r.status}</span>
                  </div>
                  <div className="space-y-3">
                    {(qset.questions || []).map((q, qi) => (
                      <div key={q.id} className="text-xs">
                        <p className="text-[#FF4343] mb-1">Q{qi + 1}: <span className="text-white">{q.prompt}</span></p>
                        <p className="text-[#9CA3AF] pl-3 whitespace-pre-wrap leading-relaxed">{r.answers?.[q.id] || <span className="text-[#6B7280] italic">(no answer)</span>}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}

